"use client";

import React, { createElement, useCallback, useEffect, useRef, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import RainforestSkeleton from "@/components/rainforest/rainforest-skeleton";
import { useRainforestTheme } from "@/components/rainforest/use-rainforest-theme";
import { methodTypeFromRainforestEvent } from "@/lib/billing/method-type-from-rainforest-event";
import { normalizeDecline } from "@/lib/payments/decline-reason";
import { loadPaymentSdk } from "@/lib/payments/load-payment-sdk";
import { registerPaymentSurface } from "@/lib/payments/payment-surface-registry";
import { isSessionAlreadyConsumed } from "@/lib/payments/session-consumed";
import { cx } from "@/lib/tailwind-utils";
import { PaymentProcessor } from "@/types/auth";
import { PaymentMethodType } from "@/types/enums/payment";
import { BinLookupResult } from "@/types/rainforest";
interface RainforestPaymentLinkProps {
  onSuccess: (payinId: string) => void;
  onFailure: (err: any) => void;
  /**
   * The payment session had already been used. The payment may exist: the
   * caller should ask the server rather than assume either outcome.
   */
  onSessionConsumed?: () => void;
  onBinLookup?: (result: BinLookupResult) => void;
  onMethodTypeChange?: (methodType: PaymentMethodType | null) => void;
  onConfigUpdateAccepted?: () => void; // Called when payin config update is accepted
  sessionKey: string;
  payinConfigId: string;
  sandbox: boolean;
  className?: string;
  /** @deprecated Use allowedMethods instead. When true, defaults to CARD,ACH,APPLE_PAY; when false, CARD,ACH. */
  allowApplePay?: boolean;
  /**
   * Comma-separated list of allowed payment methods (e.g. "CARD", "CARD,APPLE_PAY", "CARD,ACH").
   * When provided, overrides allowApplePay. Use "CARD" to disable ACH.
   */
  allowedMethods?: string;
}
const RainforestPaymentLink: React.FC<RainforestPaymentLinkProps> = ({
  onSuccess,
  onFailure,
  onSessionConsumed,
  onBinLookup,
  onMethodTypeChange,
  onConfigUpdateAccepted,
  sessionKey,
  payinConfigId,
  sandbox,
  className,
  allowApplePay = true,
  allowedMethods
}) => {
  const resolvedAllowedMethods = allowedMethods ?? (allowApplePay ? "CARD,ACH,APPLE_PAY" : "CARD,ACH");
  const [payinId, setPayinId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const rainforestTheme = useRainforestTheme();
  const componentRef = useRef<HTMLDivElement>(null);
  const isInitialMountRef = useRef(true);
  // Store the initial payinConfigId and sessionKey to pass to JSX - these never change after mount
  // This prevents React from updating the DOM attributes on re-renders (which would clear inputs)
  const initialPayinConfigIdRef = useRef(payinConfigId);
  const initialSessionKeyRef = useRef(sessionKey);
  const previousPayinConfigIdRef = useRef(payinConfigId);
  const previousSessionKeyRef = useRef(sessionKey);
  useEffect(() => {
    // See the note in rainforest-terminal.tsx: no cleanup on purpose.
    loadPaymentSdk("RAINFOREST", sandbox).catch(error => {
      console.error("Failed to load the Rainforest SDK", error);
    });
  }, [sandbox]);
  const handleApproved = useCallback(async (event: any) => {
    const newPayinId = event.detail[0].data.payin_id;
    setPayinId(newPayinId);
    setIsLoading(false);
    onSuccess(newPayinId);
  }, [onSuccess]);
  const handleDeclined = useCallback((event_0: any) => {
    setIsLoading(false);
    const event_type = event_0.detail[0]["data"]["auth_code"];
    switch (event_type) {
      case "decline_insufficient_funds":
        onFailure("The payment was declined due to insufficient funds.");
        break;
      case "decline_expired_card":
        onFailure("Payment declined, the card has expired.");
        break;
      case "decline_cvc":
        onFailure("Payment declined, invalid CVC.");
        break;
      case "decline_lost_card":
      case "decline_stolen_card":
        onFailure("Payment declined, this card has been reported lost or stolen.");
        break;
      case "decline_processing_error":
        onFailure("Payment declined, due to a processing error.");
        break;
      default:
        onFailure("Payment declined. Please try again, if this issue persists, contact us.");
    }
  }, [onFailure]);
  const handleError = useCallback((event_1: any) => {
    setIsLoading(false);

    // A session that was already consumed is the one error worth not
    // reporting as a failure - the payment may well have gone through. It is
    // handed to `onSessionConsumed` so the caller can ask the server what
    // happened. Everything else is a real failure and is said out loud: the
    // seven-substring `JSON.stringify` check this replaces swallowed matches
    // silently, leaving the caller's spinner running with no payment in
    // flight and nothing written down anywhere.
    if (isSessionAlreadyConsumed(PaymentProcessor.RAINFOREST, event_1.detail)) {
      onSessionConsumed?.();
      return;
    }
    onFailure(normalizeDecline(PaymentProcessor.RAINFOREST, event_1.detail).message);
  }, [onFailure, onSessionConsumed]);
  const handleLoaded = useCallback(() => {
    setIsLoading(false);
  }, []);
  const handleLoading = useCallback(() => {
    setIsLoading(true);
  }, []);
  const handleBinLookup = useCallback((event_2: any) => {
    const binLookupData = event_2.detail?.[0];
    // Validate event data has required structure before forwarding
    if (onBinLookup && binLookupData?.status !== undefined) {
      onBinLookup(binLookupData);
    }
  }, [onBinLookup]);
  const handleMethodUpdated = useCallback((event_3: Event) => {
    onMethodTypeChange?.(methodTypeFromRainforestEvent(event_3));
  }, [onMethodTypeChange]);
  const handleConfigUpdateAccepted = useCallback(() => {
    onConfigUpdateAccepted?.();
  }, [onConfigUpdateAccepted]);
  const handleConfigUpdateIgnored = useCallback(() => {
    // Config update was ignored by Rainforest
  }, []);

  // Register with the surface registry so callers can submit this form without
  // reaching for `document.querySelector("rainforest-payment")` - which picks
  // the *first* such element on the page, not necessarily this one.
  //
  // Registered once per mount, resolving the element only when submit is
  // actually called. Re-registering on every render would push this surface
  // back onto the top of the stack, so a page re-rendering behind an open
  // dialog would silently steal the dialog's submit.
  useEffect(() => registerPaymentSurface({
    submit: () => componentRef.current?.querySelector<HTMLElement & {
      submit: () => void;
    }>("rainforest-payment")?.submit()
  }), []);
  useEffect(() => {
    const component = componentRef.current ? componentRef.current.querySelector("rainforest-payment") : null;
    if (component) {
      component.addEventListener("approved", handleApproved);
      component.addEventListener("declined", handleDeclined);
      component.addEventListener("error", handleError);
      component.addEventListener("loaded", handleLoaded);
      component.addEventListener("loading", handleLoading);
      component.addEventListener("bin-lookup", handleBinLookup);
      component.addEventListener("method-updated", handleMethodUpdated);
      component.addEventListener("config-update-accepted", handleConfigUpdateAccepted);
      component.addEventListener("config-update-ignored", handleConfigUpdateIgnored);
      return () => {
        component.removeEventListener("approved", handleApproved);
        component.removeEventListener("declined", handleDeclined);
        component.removeEventListener("error", handleError);
        component.removeEventListener("loaded", handleLoaded);
        component.removeEventListener("loading", handleLoading);
        component.removeEventListener("bin-lookup", handleBinLookup);
        component.removeEventListener("method-updated", handleMethodUpdated);
        component.removeEventListener("config-update-accepted", handleConfigUpdateAccepted);
        component.removeEventListener("config-update-ignored", handleConfigUpdateIgnored);
      };
    }
  }, [handleApproved, handleDeclined, handleError, handleLoaded, handleLoading, handleBinLookup, handleMethodUpdated, handleConfigUpdateAccepted, handleConfigUpdateIgnored]);

  // Update payin-config-id and session-key via DOM API when they change (without component remount)
  // Uses remove/set pattern per Rainforest docs to trigger config-update-accepted event
  // Note: Initial mount is handled by JSX props, this only handles updates
  useEffect(() => {
    // Skip on initial mount - JSX already set the attributes
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousPayinConfigIdRef.current = payinConfigId;
      previousSessionKeyRef.current = sessionKey;
      return;
    }
    const component_0 = componentRef.current ? componentRef.current.querySelector("rainforest-payment") : null;
    if (!component_0) {
      return;
    }
    const configIdChanged = previousPayinConfigIdRef.current !== payinConfigId;
    const sessionKeyChanged = previousSessionKeyRef.current !== sessionKey;
    if (configIdChanged || sessionKeyChanged) {
      // Config changed - use remove/set pattern per Rainforest docs
      // Update session-key first if changed
      if (sessionKeyChanged) {
        component_0.removeAttribute("session-key");
        component_0.setAttribute("session-key", sessionKey);
        previousSessionKeyRef.current = sessionKey;
      }

      // Then update payin-config-id if changed
      if (configIdChanged) {
        component_0.removeAttribute("payin-config-id");
        component_0.setAttribute("payin-config-id", payinConfigId);
        previousPayinConfigIdRef.current = payinConfigId;
      }
    }
  }, [payinConfigId, sessionKey]);
  if (!sessionKey || !payinConfigId || payinId) {
    return payinId ? <div className="p-10 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center text-green-500">
          <FaCheckCircle size="64" className="animate-custom-bounce" />
        </div>
        <h2 className="text-h1 text-foreground">Payment Successful!</h2>
      </div> : null;
  }
  return <div className={cx("w-full", className)} ref={componentRef}>
      {/* Always render the rainforest-payment component for event listeners */}
      {/* session-key and payin-config-id use ref values (stable) - updates happen via DOM API only */}
      {/* This prevents React from remounting the component and clearing inputs when config changes */}
      {createElement("rainforest-payment", {
      "session-key": initialSessionKeyRef.current,
      "payin-config-id": initialPayinConfigIdRef.current,
      "allowed-methods": resolvedAllowedMethods,
      "style-font-family": "-apple-system, system-ui, Inter",
      style: {
        display: isLoading ? "none" : "block"
      },
      ...rainforestTheme
    } as any)}

      {/* Show loading skeleton while loading */}
      {isLoading && <RainforestSkeleton padding="md" />}
    </div>;
};
export default RainforestPaymentLink;